import { useQuery } from 'react-query';
import apiClient from '../apiClient/apiClient';
import { PatchesResponse, PatchInfo, PatchLookupMap } from './patches.types';
import { promisePool } from '../../utils/promisePool';

import { useState, useEffect } from 'react';
import { Store } from '../../types';

// Transform function to normalize patch data from API response
const transformPatchData = (patch: any): PatchInfo => {
  const transformedPatch: PatchInfo = {
    ...patch,
    // Map patchName to name for backwards compatibility
    name: patch.patchName || patch.name,
    // Operations are now flat - no GraphQL transformation needed
    operations: patch.operations || []
  };
  
  return transformedPatch;
};

export const useFranchiseStores = (franchiseCode: string) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    if (!franchiseCode) {
      setStores([]);
      setError(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    const fetchAllStores = async () => {
      setIsLoading(true);
      setError(null);
      let allStores: Store[] = [];
      let page = 0;
      let pages = 1;
      const pageSize = 100;
      try {
        while (page < pages) {
          const response = await apiClient.get(`/api/stores/by-franchise/${franchiseCode}`, {
            params: { page, pageSize }
          });
          const data = response.data;
          if (data && data.stores) {
            allStores = allStores.concat(data.stores);
            pages = data.pages || 1;
            page++;
          } else {
            break;
          }
        }
        if (!cancelled) {
          setStores(allStores);
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchAllStores();
    return () => { cancelled = true; };
  }, [franchiseCode]);

  return {
    stores,
    storeNumbers: stores.map(s => s.storeNumber),
    isLoading,
    error
  };
};

// Hook to fetch patches for given store numbers
export const usePatches = (
  storeNumbers: string[],
  type?: string,
  enabled: boolean = true
) => {
  return useQuery<PatchesResponse>(
    ['patches', storeNumbers.join(','), type],
    async () => {
      if (storeNumbers.length === 0) {
        return {
          patches: [],
          count: 0,
          page: 0,
          pageSize: 100,
          pages: 0
        };
      }
      
      const tasks = storeNumbers.map((storeNumber: string) => async () => {
        const params: any = {
          storeNumber: storeNumber.trim()
        };
        if (type) {
          params.type = type;
        }
        
        try {
          const response = await apiClient.get<PatchesResponse>('/api/patches', { params });
          const data = response.data;
          
          if (data && data.patches) {
            return data.patches.map((patch: any) => transformPatchData({
              ...patch,
              storeNumber
            }));
          }
          return [];
        } catch (error) {
          console.warn(`Failed to fetch patches for store ${storeNumber}:`, error);
          return [];
        }
      });
      
      const storeResults = await promisePool(tasks, 10);
      
      // Flatten all patches from all stores
      const allPatches = storeResults.flat();
      
      return {
        patches: allPatches,
        count: allPatches.length,
        page: 0,
        pageSize: allPatches.length,
        pages: 1
      };
    },
    {
      enabled: enabled && storeNumbers.length > 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );
};

// Hook to get patches for a specific franchise
export const useFranchisePatches = (
  franchiseCode: string,
  type: string = 'setprodprices', // Default to the type we care about
  enabled: boolean = true
) => {
  const { storeNumbers, isLoading: isLoadingStores } = useFranchiseStores(franchiseCode);
  
  return useQuery<PatchLookupMap>(
    ['franchise-patches', franchiseCode, type],
    async () => {
      if (storeNumbers.length === 0) {        return {};
      }
      let allPatches: PatchInfo[] = [];
      let page = 0;
      let pages = 1;
      const pageSize = 100;
      while (page < pages) {
        const filteredStoreNumbers = storeNumbers.filter(Boolean);
        const params: any = {
          storeNumbers: filteredStoreNumbers.join(','),
          type,
          page,
          pageSize
        };
        // Defensive: if menuChannels is present and is an array, join as comma-separated string
        if (params.menuChannels && Array.isArray(params.menuChannels)) {
          params.menuChannels = params.menuChannels.filter(Boolean).join(',');
        }
        // Defensive: if storeNumbers is present and is an array, join as comma-separated string
        if (params.storeNumbers && Array.isArray(params.storeNumbers)) {
          params.storeNumbers = params.storeNumbers.filter(Boolean).join(',');
        }
        const response = await apiClient.get<PatchesResponse>('/api/patches', { params });
        const data = response.data;
        if (data && data.patches) {
          // Transform patches to normalize data structure
          const transformedPatches = data.patches.map(transformPatchData);
          allPatches = allPatches.concat(transformedPatches);
          pages = data.pages || 1;
          page++;
        } else {
          break;
        }
      }
      return createPatchLookupMap(allPatches);
    },
    {
      enabled: enabled && !isLoadingStores && storeNumbers.length > 0,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );
};

// Create a lookup map from patch ID to store info
export const createPatchLookupMap = (patches: PatchInfo[]): PatchLookupMap => {
  const lookupMap: PatchLookupMap = {};
  
  patches.forEach(patch => {
    // Extract store number from patch name (format: "036875_SetProdPrices")
    const storeNumber = patch.name?.split('_')[0] || 'Unknown';
    
    lookupMap[patch.patchId] = {      storeNumber,
      type: patch.type || 'setprodprices',
      menuChannel: patch.menuChannel,
      patchName: patch.name // Include for debugging
    };
  });
  
  return lookupMap;
};

// Hook to create a patch lookup map for multiple patch IDs
export const usePatchLookup = (
  patchIds: string[],
  type?: string,
  enabled: boolean = true
) => {
  return useQuery<PatchLookupMap>(
    ['patch-lookup', patchIds.join(','), type],
    async () => {
      if (patchIds.length === 0) {
        return {};
      }

      // For now, we'll need to fetch patches and create the lookup
      // This is a simplified approach - in a real scenario, we might need
      // to batch requests or have a specific endpoint for this
      let allPatches: PatchInfo[] = [];
      let page = 0;
      let pages = 1;
      const pageSize = 100;
      while (page < pages) {
        const params: any = {
          page,
          pageSize
        };
        if (type) {
          params.type = type;
        }
        if (params.menuChannels && Array.isArray(params.menuChannels)) {
          params.menuChannels = params.menuChannels.filter(Boolean).join(',');
        }
        if (params.storeNumbers && Array.isArray(params.storeNumbers)) {
          params.storeNumbers = params.storeNumbers.filter(Boolean).join(',');
        }
        const response = await apiClient.get<PatchesResponse>('/api/patches', { params });
        const data = response.data;
        if (data && data.patches) {
          // Transform patches to normalize data structure
          const transformedPatches = data.patches.map(transformPatchData);
          allPatches = allPatches.concat(transformedPatches);
          pages = data.pages || 1;
          page++;
        } else {
          break;
        }
      }
      const patches = allPatches.filter((patch: PatchInfo) =>
        patchIds.includes(patch.patchId)
      );
      return createPatchLookupMap(patches);
    },
    {
      enabled: enabled && patchIds.length > 0,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );
};

// Hook for prioritized patch loading - loads needed patches first, then all
export const usePrioritizedPatches = (
  franchiseCode: string,
  neededPatchIds: string[] = [],
  type: string = 'setprodprices',
  enabled: boolean = true
) => {
  const { storeNumbers, isLoading: isLoadingStores } = useFranchiseStores(franchiseCode);
  
  // First, try to get just the patches we need immediately (if we can identify specific stores)
  const { data: priorityPatchMap, isLoading: isPriorityLoading } = useQuery<PatchLookupMap>(
    ['priority-patches', franchiseCode, neededPatchIds.slice(0, 20).sort(), type],
    async () => {
      if (storeNumbers.length === 0 || neededPatchIds.length === 0) {
        return {};
      }

      // Load patches in smaller batches to find our needed patches faster
      const batchSize = Math.min(10, storeNumbers.length);
      const batches = [];
      
      for (let i = 0; i < storeNumbers.length; i += batchSize) {
        batches.push(storeNumbers.slice(i, i + batchSize));
      }      
      const priorityPatches: PatchInfo[] = [];
      
      // Process batches until we find most of our needed patches
      for (const batch of batches.slice(0, 3)) { // Check first 3 batches (30 stores max)
        let batchPatches: PatchInfo[] = [];
        let pageNum = 0;
        let totalPages = 1;
        const pageSize = 100;
        while (pageNum < totalPages) {
          const filteredBatch = batch.filter(Boolean);
          const params: any = {
            storeNumbers: filteredBatch.join(','),
            type,
            page: pageNum,
            pageSize
          };
          if (params.menuChannels && Array.isArray(params.menuChannels)) {
            params.menuChannels = params.menuChannels.filter(Boolean).join(',');
          }
          if (params.storeNumbers && Array.isArray(params.storeNumbers)) {
            params.storeNumbers = params.storeNumbers.filter(Boolean).join(',');
          }
          const response = await apiClient.get<PatchesResponse>('/api/patches', { params });
          const data = response.data;
          if (data && data.patches) {
            batchPatches = batchPatches.concat(data.patches);
            totalPages = data.pages || 1;
            pageNum++;
          } else {
            break;
          }
        }
        priorityPatches.push(...batchPatches);
        
        // Check if we found most of our needed patches
        const foundPatchIds = priorityPatches.map(p => p.patchId);
        const foundCount = neededPatchIds.filter(id => foundPatchIds.includes(id)).length;
        
        // If we found most of what we need, stop early
        if (foundCount >= neededPatchIds.length * 0.8) {
          break;
        }
      }
      
      return createPatchLookupMap(priorityPatches);
    },
    {
      enabled: enabled && !isLoadingStores && storeNumbers.length > 0 && neededPatchIds.length > 0,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  // Background loading of all patches for the franchise
  const { data: allPatchMap, isLoading: isBackgroundLoading } = useFranchisePatches(
    franchiseCode,
    type,
    enabled
  );
  
  // Use priority map if we're still loading complete data, otherwise use complete map
  const activePatchMap = allPatchMap || priorityPatchMap || {};
  const isLoading = isPriorityLoading && !priorityPatchMap;
  
  return {
    data: activePatchMap,
    isLoading,
    isBackgroundLoading,
    hasPriorityData: !!priorityPatchMap,
    hasCompleteData: !!allPatchMap,
    priorityLoadProgress: priorityPatchMap ? 
      neededPatchIds.filter(id => priorityPatchMap[id]).length / Math.max(neededPatchIds.length, 1) : 0
  };
};
